<script setup lang="ts">
import Dayjs from '#build/dayjs.imports.mjs'

interface Props {
  open?: boolean
}

const props = defineProps<Props>()

const searchStore = useSearchv2Store()
const configStore = useConfigStore()
const navStore = useNavStore()
const dayjs = useDayjs()
const isOpen = ref(props.open)
const choseStartDate = ref(true)
const choseEndDate = ref(false)

const dateToUse = computed(() => {
  if (searchStore.getStartDate) return searchStore.getStartDate
  return navStore.activeFunnelDefaultDate || configStore.defaultCalenderDate
})

const currentMonth = ref<Dayjs.Dayjs>(dayjs(dateToUse.value).startOf('month'))
if (searchStore.getStartDate)
  currentMonth.value = dayjs(searchStore.getStartDate, 'YYYY-MM-DD').startOf(
    'month'
  )

const localStartDate = ref(dayjs(dateToUse.value, 'YYYY-MM-DD'))
const localEndDate = ref(
  dayjs(
    searchStore.getEndDate
      ? searchStore.getEndDate
      : dayjs(dateToUse.value).add(7, 'days').format('YYYY-MM-DD'),
    'YYYY-MM-DD'
  )
)

const updateStartDate = (date: Dayjs.Dayjs) => {
  localStartDate.value = date
  choseStartDate.value = false
  choseEndDate.value = true
}

const updateEndDate = (date: Dayjs.Dayjs) => {
  localEndDate.value = date
  choseStartDate.value = true
  choseEndDate.value = false
}

const handleSelectDate = (date: Dayjs.Dayjs) => {
  if (choseStartDate.value) {
    updateStartDate(date)
    localEndDate.value = dayjs(null)
    return
  }
  if (choseEndDate.value) {
    if (date <= localStartDate.value) {
      updateStartDate(date)

      return
    } else {
      updateEndDate(date)
    }
    return
  }
}

const handleApplySearch = () => {
  searchStore.setDate(localStartDate.value.format('YYYY-MM-DD'))
  searchStore.setDuration(localEndDate.value.diff(localStartDate.value, 'days'))
  searchStore.setSearchPartyCookie()

  isOpen.value = false
  emits('apply-search')
}

const resetDates = () => {
  localStartDate.value = dayjs(dateToUse.value, 'YYYY-MM-DD')
  localEndDate.value = dayjs(
    searchStore.getEndDate
      ? searchStore.getEndDate
      : dayjs(dateToUse.value).add(7, 'days').format('YYYY-MM-DD'),
    'YYYY-MM-DD'
  )
}

const close = () => {
  isOpen.value = false
  emits('close')
}

const formatedStartDate = computed(() => {
  if (localStartDate.value.isValid())
    return dayjs(localStartDate.value, 'YYYY-MM-DD').format('MMM D - YYYY')
  return ''
})

const formatedEndDate = computed(() => {
  if (localEndDate.value.isValid())
    return dayjs(localEndDate.value, 'YYYY-MM-DD').format('MMM D - YYYY')
  return ''
})

const startDateValid = computed(() => localStartDate.value.isValid())
const endDateValid = computed(() => localEndDate.value.isValid())

const amountOfNights = computed(() => {
  if (startDateValid.value && endDateValid.value)
    return dayjs(localEndDate.value).diff(dayjs(localStartDate.value), 'days')
  return 0
})
watch(
  () => isOpen.value,
  () => {
    emits('toggle')
  }
)

const emits = defineEmits(['apply-search', 'close', 'toggle'])
</script>

<template>
  <slot name="button" :reset-dates="resetDates" :open="() => (isOpen = true)">
    <VButton
      variant="unstyled"
      class="w-full bg-brand-white border border-brand-secondary-400 hover:bg-brand-gray-100 px-2.5 py-2.5 shadow-custom flex flex-row items-center gap-x-2 justify-between text-brand-secondary-500"
      @click="resetDates(), (isOpen = true)"
    >
      <span>{{ searchStore.getFormattedSearchInterval }}</span>
      <NuxtImg height="20" width="20" src="icons/misc/calender.svg" />
    </VButton>
  </slot>
  <ResponsiveDialogWrapper
    :open="isOpen"
    :modal-props="{
      whiteCloseButton: true,
      closeOnOutsideClick: true,
      wide: true,
    }"
    :drawer-props="{
      direction: 'top',
      isOpen: isOpen,
      showButtons: true,
      whiteCloseButton: true,
    }"
    @close="isOpen = false"
  >
    <template #header>
      <div class="bg-brand-white shadow-custom z-10">
        <div class="p-md bg-brand-secondary-900">
          <h4 class="font-h4 text-brand-white">
            {{ $t('calender.edit-dates') }}
          </h4>
        </div>
        <CalenderDaysLabel
          class="pl-10 justify-center border-t broder-brand-white"
        />
      </div>
    </template>

    <div
      class="flex flex-col gap-4 justify-center items-center bg-brand-white py-4"
    >
      <CalenderGrid
        v-for="i in 25"
        :key="i"
        :selected-month="
          $dayjs()
            .add(i - 1, 'month')
            .isSame(localStartDate, 'month')
        "
        :show-days-label="false"
        :dates="$dayjs().add(i - 1, 'month')"
        :start-date="localStartDate"
        :end-date="localEndDate"
        @select-date="handleSelectDate"
      />
    </div>
    <template #footer>
      <div
        class="flex flex-col gap-y-4 items-start py-5 px-6 bg-brand-white shadow-custom-top"
      >
        <div class="flex gap-4 w-full justify-evenly">
          <div
            class="min-w-[30%] self-start flex flex-col pr-4 border-r-[1px] border-brand-gray-200 text-brand-gray-500"
          >
            <div class="flex gap-5 justify-between flex-row">
              <p>{{ $t('calender.arrival') }}:</p>
              <p class="text-brand-secondary-400">{{ formatedStartDate }}</p>
            </div>
            <div class="flex gap-5 justify-between flex-row">
              <p>{{ $t('calender.departure') }}:</p>
              <p class="text-brand-secondary-400">{{ formatedEndDate }}</p>
            </div>
          </div>
          <div
            class="flex flex-row gap-2 justify-center items-center text-brand-gray-500"
          >
            <Icon name="material-symbols:dark-mode-outline-rounded" size="22" />

            <div class="text-center">
              {{ amountOfNights }}
              {{ $t('booking.details.nights', amountOfNights) }}
            </div>
          </div>
        </div>

        <div class="flex gap-4 w-full">
          <VButton
            class="!w-full justify-center"
            variant="alternative"
            @click="close"
            >{{ $t('common.cancel') }}</VButton
          >
          <VButton
            :disabled="!startDateValid || !endDateValid"
            class="!w-full justify-center"
            @click="handleApplySearch"
            >{{ $t('common.apply') }}</VButton
          >
        </div>
      </div>
    </template>
  </ResponsiveDialogWrapper>
</template>
